import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Calendar, Check, Send, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { motion, type Variants } from "framer-motion";
import { SiteLayout } from "@/components/site/Layout";
import { ServicePageOutro } from "@/components/sections/ServicePageOutro";
import { BookAppointmentLink } from "@/components/site/BookAppointmentLink";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import thumb1 from "@/assets/service-pediatric-240.webp";
import thumb2 from "@/assets/service-preventative-240.webp";
import thumb3 from "@/assets/service-xray-240.webp";
import heroAsset from "@/assets/oral-surgery-pickering.jpg.asset.json";
const heroImg = heroAsset.url;
import proceduresAsset from "@/assets/oral-surgery-extraction-pickering.jpg.asset.json";
import { loadDashboardSeo, mergeSeoHead } from "@/lib/dashboard-seo";
import { getService } from "@/lib/dashboard";
import { dashboardServiceOr } from "@/components/services/DashboardServiceView";
const proceduresImg = proceduresAsset.url;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const } },
};

const relatedServices = [
  {
    to: "/services/general-dentistry/tooth-extraction-pickering-on" as const,
    title: "Tooth Extraction",
    desc: "Removal of teeth that cannot be saved, with local numbing and aftercare guidance.",
    img: thumb1,
  },
  {
    to: "/services/general-dentistry/wisdom-teeth-removal-pickering-on" as const,
    title: "Wisdom Tooth Removal",
    desc: "Surgical assessment and removal of impacted or problematic wisdom teeth.",
    img: thumb2,
  },
  {
    to: "/services/restorative-dentistry/dental-implants-pickering-on" as const,
    title: "Dental Implants",
    desc: "Long-lasting tooth replacement supported by surgical implant preparation.",
    img: thumb3,
  },
];

const faqs = [
  { q: "What is oral surgery?", a: "Oral surgery includes surgical dental treatments that go beyond routine procedures." },
  { q: "What does oral surgery mean?", a: "It means an intervention is required when surgical access is needed for proper treatment, or when infection extends below the gumline." },
  { q: "When is oral surgery recommended?", a: "It is recommended when teeth are impacted or difficult to remove, or when bone and jaw involvement is present. The decision is based on clinical evaluation and imaging." },
  { q: "What procedures are included in oral surgery?", a: "Procedures include complex tooth extractions, impacted teeth removal, wisdom tooth removal, bone grafting, and surgical implant preparation." },
  { q: "Is wisdom tooth removal considered oral surgery?", a: "Yes, wisdom tooth removal is officially classified as oral surgery." },
  { q: "Is dental implant placement considered oral surgery?", a: "Yes, surgical implant preparation and bone grafting fall under oral surgery." },
  { q: "What happens during an oral surgery consultation?", a: "During your oral surgery consultation, we perform a clinical evaluation and take diagnostic imaging to base our treatment decisions. The procedures are then explained clearly before treatment." },
];

const PAGE_URL =
  "https://baylydentalclinic.com/services/general-dentistry/oral-surgery-pickering-on";
const META_TITLE = "Oral Surgery in Pickering, ON | Bayly Dental Clinic";
const META_DESC =
  "Oral surgery in Pickering for complex tooth extractions, wisdom tooth removal, bone grafting, and surgical implant preparation. Book your consultation today.";
const EXCERPT =
  "Learn about our comprehensive oral surgery services in Pickering, including complex tooth extractions, bone grafting, and surgical implant preparation tailored to your clinical evaluation.";

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": "https://baylydentalclinic.com/oral-surgery-pickering/",
  },
  headline: "Oral Surgery in Pickering, ON",
  description:
    "Oral surgery in Pickering for complex tooth extractions, wisdom tooth removal, bone grafting, and surgical implant preparation.",
  publisher: {
    "@type": "Dentist",
    name: "Bayly Dental Clinic",
    logo: {
      "@type": "ImageObject",
      url: "https://baylydentalclinic.com/logo.png",
    },
  },
  about: {
    "@type": "MedicalTherapy",
    name: "Oral Surgery",
    relevantSpecialty: {
      "@type": "MedicalSpecialty",
      name: "Surgical Dentistry",
    },
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const Route = createFileRoute(
  "/services_/general-dentistry_/oral-surgery-pickering-on",
)({
  loader: async () => { const [seo, svc] = await Promise.all([loadDashboardSeo("/services/general-dentistry/oral-surgery-pickering-on"), getService("/services/general-dentistry/oral-surgery-pickering-on")]); return { seo, service: svc?.data?.published_payload ?? null }; },
  head: ({ loaderData }) => mergeSeoHead(loaderData?.seo, ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { name: "excerpt", content: EXCERPT },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImg },
      { property: "twitter:image", content: heroImg },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(webPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
    ],
  })),
  component: dashboardServiceOr("/services_/general-dentistry_/oral-surgery-pickering-on", OralSurgeryPage),
});

function SidebarBookingForm() {
  return (
    <div className="bg-card rounded-2xl p-5 shadow-[var(--shadow-card)] border border-border text-center">
      <span className="eyebrow">Quick Booking</span>
      <h3 className="mt-2 text-lg font-bold text-ink leading-snug">Book your appointment</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        We'll get back to you within one business day.
      </p>
      <BookAppointmentLink className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-primary-foreground font-bold py-3 rounded-full shadow-md shadow-primary/20 transition-all hover:-translate-y-0.5 text-sm">
        <Calendar className="h-4 w-4" /> Book Appointment
      </BookAppointmentLink>
    </div>
  );
}

const keyTakeaways = [
  <><strong>Targeted Treatment:</strong> Oral surgery addresses conditions requiring surgical access for proper treatment.</>,
  <><strong>Customized Planning:</strong> Decisions are based entirely on clinical evaluation and diagnostic imaging.</>,
  <><strong>Comprehensive Procedures:</strong> Care includes complex tooth extraction, wisdom tooth removal, and surgical implant preparation.</>,
  <><strong>Patient Comfort First:</strong> Local anesthesia prevents pain during surgery, with sedation options available when appropriate.</>,
  <><strong>Transparent Guidance:</strong> Procedures, costs, and insurance coverage are reviewed clearly before treatment begins.</>,
];

const P = "text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed";
const H2 = "text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight";
const H3 = "text-2xl md:text-3xl font-bold text-ink mt-8 leading-tight tracking-tight";

function OralSurgeryPage() {
  return (
    <SiteLayout>
      <section className="bg-surface">
        <div className="container-1120 py-10 md:py-14 text-center">
          <nav aria-label="breadcrumb" className="flex justify-center">
            <ol className="eyebrow flex flex-wrap items-center justify-center gap-2 bg-accent/70 text-ink/70 rounded-full px-5 py-2">
              <li><Link to="/" className="hover:text-primary transition">Home</Link></li>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <li><Link to="/services" className="hover:text-primary transition">Services</Link></li>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <li><Link to="/services/general-dentistry" className="hover:text-primary transition">General Dentistry</Link></li>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <li aria-current="page" className="text-ink">Oral Surgery</li>
            </ol>
          </nav>
          <h1 className="mt-4 text-4xl md:text-5xl font-bold text-ink max-w-3xl mx-auto leading-tight">
            Oral Surgery in Pickering, ON
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            {EXCERPT}
          </p>
        </div>
      </section>

      <section className="relative section-pad overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-soft)" }} />
        <div className="container-1120 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:items-start">
          <motion.article
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: "some" }}
            className="lg:col-span-2"
          >
            <div className="overflow-hidden rounded-3xl shadow-[var(--shadow-card)] mb-8">
              <img
                src={heroImg}
                alt="Oral surgery in Pickering performed by dental team at Bayly Dental Clinic"
                title="Oral Surgery in Pickering - Expert Surgical Dental Care at Bayly Dental Clinic"
                data-caption="Precision oral surgery in Pickering, performed by the experienced surgical team at Bayly Dental Clinic."
                data-description="Oral surgery in Pickering at Bayly Dental Clinic - two gowned and gloved dental surgeons performing a precise surgical procedure on a relaxed, sedated patient in a fully sterile operatory. This image represents the advanced oral surgery services we provide, including complex tooth extractions, impacted wisdom teeth removal, bone grafting, exposure of impacted teeth, and surgical site preparation for dental implants. Serving Pickering, Pickering, Whitby, Oshawa, and Markham, ON with sedation options, digital imaging, and gentle, evidence-based surgical protocols for a calm, comfortable experience."
                width={1200}
                height={800}
                loading="eager"
                className="w-full h-auto object-cover aspect-[16/9]"
              />
            </div>
            <div id="service-content" className="space-y-6 text-ink leading-relaxed">
              <p className={P}>
                <strong>Oral surgery</strong> involves surgical dental treatments that go beyond routine procedures. At Bayly Dental Clinic, oral surgery encompasses complex tooth extractions, wisdom teeth removal, bone grafting, and surgical preparation for dental implants. All recommendations are based on thorough clinical evaluation and imaging.
              </p>

              <h2 className={H2}>Understanding Oral Surgery</h2>
              <ul className="space-y-4 my-6">
                {keyTakeaways.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-accent">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className={P}>{item}</span>
                  </li>
                ))}
              </ul>
              <p className={P}>
                When routine dental treatments are not sufficient to resolve an underlying issue, <strong>dental surgery</strong> provides the necessary intervention to restore your oral health and function. Whether you are experiencing discomfort from an impacted tooth or require foundational work to replace missing teeth, finding a skilled <strong>oral surgeon</strong> or experienced <strong>dental surgeon</strong> is an important step. At Bayly Dental Clinic, we provide comprehensive <strong>oral surgery in Pickering</strong>, utilizing advanced diagnostic evaluation to deliver individualized care.
              </p>

              <h2 className={H2}>What Is Oral Surgery?</h2>
              <p className={P}>
                Oral surgery includes surgical dental treatments that go beyond routine procedures.
              </p>
              <p className={P}>
                An <strong>oral surgery consultation</strong> allows your dentist to thoroughly review your symptoms and determine if surgical access is required for proper treatment. Every procedure is explained clearly to you before treatment, ensuring you understand why the surgery is necessary and what to expect.
              </p>

              <h2 className={H2}>When Might Oral Surgery Be Recommended?</h2>
              <p className={P}>
                Oral surgery may be recommended when clinical evaluation and imaging indicate that a condition requires surgical intervention.
              </p>
              <p className={P}>Our team typically recommends an <strong>oral surgery procedure</strong> when:</p>
              <ul className="space-y-4 my-6">
                {[
                  "Teeth are impacted or difficult to remove.",
                  "Infection or damage extends below the gumline.",
                  "Bone or jaw involvement is present.",
                  "Surgical access is required for proper treatment.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-accent">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className={P}>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className={H3}>Impacted Tooth Extraction</h3>
              <p className={P}>
                When a tooth is impacted or difficult to remove, a surgical approach is required for safe extraction. You can learn more about our general extraction process on our{" "}
                <Link to="/services/general-dentistry/tooth-extraction-pickering-on" className="text-primary font-semibold hover:underline">
                  Tooth Extraction
                </Link>{" "}
                page.
              </p>

              <h3 className={H3}>Wisdom Tooth Removal</h3>
              <p className={P}>
                <strong>Wisdom teeth removal</strong> is classified as oral surgery. These teeth often require surgical extraction if they become impacted. Discover more on our{" "}
                <Link to="/services/general-dentistry/wisdom-teeth-removal-pickering-on" className="text-primary font-semibold hover:underline">
                  Wisdom Tooth Extraction
                </Link>{" "}
                page.
              </p>

              <h3 className={H3}>Dental Implant Treatment</h3>
              <p className={P}>
                If you are replacing missing teeth, oral surgery is used for surgical implant preparation. Read our{" "}
                <Link to="/services/restorative-dentistry/dental-implants-pickering-on" className="text-primary font-semibold hover:underline">
                  Dental Implants
                </Link>{" "}
                guide for more details.
              </p>

              <h3 className={H3}>Bone Grafting Procedures</h3>
              <p className={P}>
                When the jawbone lacks the density to support an implant or when bone involvement is present, <strong>bone grafting</strong> is performed. Explore our Bone Grafting services.
              </p>

              <h3 className={H3}>Sinus Lift Procedures</h3>
              <p className={P}>
                A <strong>sinus lift</strong> is a specific type of oral surgery that increases bone height in the upper jaw, creating sufficient bone volume beneath the sinus membrane to successfully place dental implants. Learn more about our{" "}
                <Link to="/services/restorative-dentistry/sinus-lift-pickering-on" className="text-primary font-semibold hover:underline">
                  Sinus Lift
                </Link>{" "}
                procedures.
              </p>

              <h2 className={H2}>What Procedures Are Included in Oral Surgery?</h2>
              <p className={P}>
                Oral surgery includes complex tooth extractions, impacted teeth removal, wisdom tooth extraction, bone grafting, and surgical implant preparation. If you are searching for <strong>oral surgery near me</strong>, our clinic provides these specialized treatments based on careful evaluation.
              </p>

              <img
                src={proceduresImg}
                alt="Oral surgery in Pickering - dentist performing a surgical tooth extraction under local anesthesia at Bayly Dental Clinic"
                title="Oral Surgery in Pickering - Surgical Tooth Extraction Procedure at Bayly Dental Clinic"
                data-caption="Surgical tooth extraction during oral surgery in Pickering at Bayly Dental Clinic."
                data-description="Oral surgery in Pickering at Bayly Dental Clinic - close-up of a calm female patient receiving a precise surgical tooth extraction performed by a gloved, masked dental surgeon using sterile instruments and local anesthesia. The image illustrates the controlled, gentle approach used during complex extractions, impacted tooth removal, wisdom tooth surgery, and surgical implant preparation. Serving patients across Pickering, Pickering, Whitby, Oshawa, and Markham, ON, our team combines digital imaging, sedation options, and evidence-based surgical protocols to ensure a comfortable, safe oral surgery experience with predictable healing and clear aftercare guidance."
                width={1200}
                height={800}
                loading="lazy"
                className="w-full h-auto rounded-2xl shadow-[var(--shadow-soft)] my-6"
              />

              <h2 className={H2}>How Does Bayly Dental Clinic Determine If Oral Surgery Is Needed?</h2>
              <p className={P}>
                Treatment recommendations and decisions are based strictly on clinical evaluation and diagnostic imaging.
              </p>
              <p className={P}>
                Before any surgical intervention is recommended, our team conducts a detailed clinical examination. We utilize imaging to accurately view your tooth position, bone structure, and whether infection extends below the gumline. The procedure and its complexity are explained clearly to you before any treatment begins.
              </p>

              <h2 className={H2}>Is Oral Surgery Painful and How Is Recovery Planned?</h2>
              <p className={P}>
                During the procedure, <strong>local anesthesia</strong> is used to prevent pain, meaning most patients report feeling a sensation of pressure rather than pain. We also make <strong>sedation options</strong> available when appropriate to help patients relax.
              </p>
              <p className={P}>
                Following the procedure, post-surgical discomfort, swelling, and soreness are explained in advance as normal.
              </p>
              <ul className="space-y-4 my-6">
                {[
                  <><strong>Initial Healing:</strong> Typically takes 3 to 10 days.</>,
                  <><strong>Full Recovery:</strong> May take 12 to 20 weeks.</>,
                  <><strong>Factors:</strong> Recovery heavily depends on the specific procedure type and its complexity.</>,
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-accent">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className={P}>{item}</span>
                  </li>
                ))}
              </ul>
              <p className={P}>
                We provide pain management guidance and review aftercare instructions in detail to support your healing.
              </p>

              <h2 className={H2}>Why Choose Bayly Dental Clinic for Oral Surgery in Pickering?</h2>
              <p className={P}>
                Bayly Dental Clinic provides individualized oral surgery treatment planning supported by clinical evaluation and imaging.
              </p>
              <p className={P}>
                Our clinic is dedicated to offering transparent, supportive care. We clearly explain <strong>oral surgery costs</strong>, noting that pricing varies by procedure type and difficulty, with wisdom tooth surgery costs discussed separately. We also review your insurance coverage before any treatment begins. Whether your needs arise from an urgent situation requiring{" "}
                <Link to="/services/general-dentistry/emergency-dentist-pickering-on" className="text-primary font-semibold hover:underline">
                  Emergency Dentistry
                </Link>{" "}
                or are coordinated through our{" "}
                <Link to="/services/general-dentistry/family-dentist-pickering-on" className="text-primary font-semibold hover:underline">
                  Family Dentistry
                </Link>{" "}
                services, our goal is to provide a highly professional surgical experience.
              </p>

              <h2 className={H2}>Common Oral Surgery Procedures</h2>
              <div className="overflow-x-auto rounded-2xl border border-border my-6">
                <table className="w-full text-left text-base">
                  <thead className="bg-accent/60">
                    <tr>
                      <th className="px-4 py-3 font-bold text-ink">Procedure</th>
                      <th className="px-4 py-3 font-bold text-ink">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr><td className="px-4 py-3 font-semibold">Complex Tooth Extraction</td><td className="px-4 py-3 text-ink/80">Remove teeth that are impacted or difficult to remove.</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Wisdom Tooth Extraction</td><td className="px-4 py-3 text-ink/80">Remove problematic third molars, officially classified as oral surgery.</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Surgical Implant Preparation</td><td className="px-4 py-3 text-ink/80">Surgically prepare the jaw for dental implants.</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Bone Grafting</td><td className="px-4 py-3 text-ink/80">Address cases where bone or jaw involvement is present.</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Sinus Lift</td><td className="px-4 py-3 text-ink/80">Increase bone height in the upper jaw beneath the sinus membrane.</td></tr>
                  </tbody>
                </table>
              </div>

              <h2 className={H2}>FAQs About Oral Surgery</h2>
              <div className="my-6">
                <Accordion type="single" collapsible className="space-y-3">
                  {faqs.map((f) => (
                    <AccordionItem
                      key={f.q}
                      value={f.q}
                      className="rounded-xl border border-border bg-card overflow-hidden px-0 shadow-[var(--shadow-card)]"
                    >
                      <AccordionTrigger className="px-5 py-4 text-lg font-semibold text-ink hover:no-underline text-left">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-4 text-muted-foreground leading-relaxed text-base">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              <h2 className={H2}>Discuss Your Oral Surgery Needs With Our Team</h2>
              <p className={P}>
                <strong>Take the First Step Toward Restoring Your Oral Health.</strong> If you have been advised that you may need a tooth extraction, wisdom tooth removal, dental implant treatment, or bone grafting, <Link to="/" className="text-primary font-semibold hover:underline">Bayly Dental Clinic</Link> can properly evaluate your needs through a comprehensive clinical evaluation and imaging.
              </p>
              <ul className="space-y-4 my-6">
                {[
                  <><strong>Comprehensive Surgical Treatment Planning:</strong> Benefit from care tailored entirely to your clinical needs, with procedures explained clearly in advance.</>,
                  <><strong>Patient Comfort:</strong> We utilize local anesthesia to prevent pain and offer sedation options when appropriate.</>,
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-accent">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className={P}>{item}</span>
                  </li>
                ))}
              </ul>
              <p className={P}>
                Schedule an oral surgery consultation at Bayly Dental Clinic today to learn whether dental surgery may be recommended as part of your customized treatment plan. Use our booking tools below to secure your preferred appointment time.
              </p>
            </div>
          </motion.article>

          <aside className="lg:self-start flex flex-col gap-5">
            <SidebarBookingForm />
            <h3 className="text-lg font-bold text-ink px-1 mt-2">Other services</h3>
            <div className="flex flex-col gap-4">
              {relatedServices.map((s) => (
                <Link
                  key={s.title}
                  to={s.to}
                  className="group bg-card rounded-2xl p-3 shadow-[var(--shadow-card)] border border-border flex gap-3 items-center transition-transform hover:-translate-y-1"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl">
                    <img src={s.img} alt={s.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base sm:text-sm font-bold text-ink leading-snug">{s.title}</h4>
                    <p className="mt-1 text-sm sm:text-xs text-muted-foreground leading-relaxed line-clamp-2">{s.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-primary shrink-0" />
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <ServicePageOutro
        heading="Ready to discuss oral surgery?"
        subtext="Book an oral surgery consultation in Pickering. Gentle care and clear treatment plans."
      />
    </SiteLayout>
  );
}